<template>
  <v-card class="mx-auto" width="90%" style="margin-bottom: 15px;">
    <v-container fluid class="my-5">
      <v-layout row justify-center>
        <v-row>
          <v-col cols="12">
            <v-data-table
              :headers="headers"
              :items="news"
              sort-by="name"
              class="elevation-1"
            >
              <template v-slot:top>
                <v-toolbar
                  flat
                >
                  <v-toolbar-title>{{ $t('views.adminNews.title') }}</v-toolbar-title>
                  <v-divider
                    class="mx-4"
                    inset
                    vertical
                  ></v-divider>
                  <v-spacer></v-spacer>
                  <v-dialog
                    v-model="dialog"
                    max-width="800px"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        color="primary"
                        dark
                        class="mb-2"
                        v-bind="attrs"
                        v-on="on"
                      >
                        {{ $t('views.adminNews.createPost') }}
                      </v-btn>
                    </template>
                    <v-card>
                      <v-card-title>
                        <span class="text-h5">{{ $t('views.adminNews.createPost') }}</span>
                      </v-card-title>
                      <v-card-text>
                        <v-container>
                          <v-row>
                            <v-col cols="12">
                              <v-text-field v-model="newPostTitle" :label="$t('views.adminNews.titleLabel')" required autofocus></v-text-field>
                              <span class="checkbox-inline"><v-checkbox v-model="newPostProfileViewer" :label="$t('views.adminNews.viewerLabel')"></v-checkbox></span>
                              <span class="checkbox-inline"><v-checkbox v-model="newPostProfileCustomizer" :label="$t('views.adminNews.customizerLabel')"></v-checkbox></span>
                              <span class="checkbox-inline"><v-checkbox v-model="newPostProfileManager" :label="$t('views.adminNews.managerLabel')"></v-checkbox></span>
                              <v-textarea v-model="newPostContent" outlined :label="$t('views.adminNews.contentLabel')" required auto-grow rows="10" />
                            </v-col>
                          </v-row>
                        </v-container>
                      </v-card-text>
                      <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn
                          color="gray"
                          text
                          @click="close"
                        >
                          {{ $t('views.adminNews.close') }}
                        </v-btn>
                        <v-btn
                          color="blue darken-1"
                          text
                          @click="save"
                        >
                          {{ $t('views.adminNews.save') }}
                        </v-btn>
                      </v-card-actions>
                    </v-card>
                  </v-dialog>
                </v-toolbar>
              </template>
              <template v-slot:item.profiles="{ item }">
                <span class="mr-1" v-for="profile in item.profiles" :key="profile">
                  <v-chip small>{{ profile }}</v-chip>
                </span>
              </template>
              <template v-slot:item.dateCreated="{ item }">
                {{ formatDateFromNow(item.dateCreated) }}
              </template>
              <template v-slot:item.content="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-bind="attrs" v-on="on">
                      {{ truncatedContent(item) }}
                    </span>
                  </template>
                  <span>{{ item.content }}</span>
                </v-tooltip>
              </template>
              <template v-slot:item.actions="{ item }">
                <v-icon small @click="deletePost(item)">mdi-delete</v-icon>
              </template>
              <template v-slot:no-data>
                {{ $t('views.adminNews.noData') }}
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-layout>
    </v-container>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment'

export default {
  data () {
    return {
      newPostTitle: '',
      newPostContent: '',
      newPostProfileManager: false,
      newPostProfileCustomizer: false,
      newPostProfileViewer: false,
      dialog: false
    }
  },
  methods: {
    ...mapActions('auth', [
      'createNewsPost',
      'loadNews',
      'deleteNewsPost'
    ]),
    deletePost (item) {
      if (confirm(this.$t('views.adminNews.deleteConfirm'))) {
        console.log(item)
        this.deleteNewsPost(item.id)
          .then(() => {
            // Refresh the news list after deleting the post
            return this.loadNews({
              isManager: true,
              isCustomizer: true
            })
          })
          .catch(error => {
            console.error('Error deleting news post:', error)
          })
      }
    },
    formatDateFromNow (firebaseTimestamp) {
      if (firebaseTimestamp) {
        const date = new Date(firebaseTimestamp.seconds * 1000 + firebaseTimestamp.nanoseconds / 1000000)
        return moment(date).fromNow()
      } else {
        return this.$t('views.adminNews.notAvailable')
      }
    },
    save () {
      this.createNewsPost({
        title: this.newPostTitle,
        content: this.newPostContent,
        profileViewer: this.newPostProfileViewer,
        profileCustomizer: this.newPostProfileCustomizer,
        profileManager: this.newPostProfileManager
      })
        .then(() => {
          // Clear the form fields after successful save
          this.newPostTitle = ''
          this.newPostContent = ''
          this.newPostProfileViewer = false
          this.newPostProfileCustomizer = false
          this.newPostProfileManager = false

          this.dialog = false
          // Refresh the news list after creating a new post
          return this.loadNews({
            isManager: true,
            isCustomizer: true
          })
        })
        .catch(error => {
          console.error('Error saving news post:', error)
        })
    },
    close () {
      this.dialog = false
    }
  },
  computed: {
    ...mapGetters('auth', [
      'news'
    ]),
    headers () {
      return [
        { text: this.$t('views.adminNews.headerTitle'), value: 'title' },
        {
          text: this.$t('views.adminNews.headerContent'),
          align: 'start',
          sortable: false,
          value: 'content'
        },
        {
          text: this.$t('views.adminNews.headerProfiles'),
          align: 'start',
          sortable: false,
          value: 'profiles'
        },
        { text: this.$t('views.adminNews.headerCreator'), value: 'createdByName' },
        { text: this.$t('views.adminNews.headerCreated'), value: 'dateCreated' },
        { text: this.$t('views.adminNews.headerActions'), value: 'actions', sortable: false }
      ]
    },
    truncatedContent () {
      return (item) => {
        if (item.content.length > 20) {
          return item.content.substring(0, 20) + '...'
        } else {
          return item.content
        }
      }
    }
  },
  watch: {},
  mounted () {
    // Load news when the component is mounted
    this.loadNews({
      isManager: true,
      isCustomizer: true
    })
  }
}
</script>
<style scoped>
  .checkbox-inline {
    display: inline-block;
    margin-right: 20px;
  }
</style>
